import React from "react";
import { debounce } from "lodash";
import AsyncCreatableSelect from "react-select/async-creatable";

import {
  GeoLocation,
  GeoService,
  IGeoService
} from "../../../services/geo.service";

import AppContainer from "../../../container";
import Loader from "../../loader";

import {
  getSelectValueProps,
  ObjectHash,
  SelectOption
} from "../../../utils/helpers";

import Colors from "../../../styles/colors.module.scss";
import { customReactSelectTheme, customStyle } from "./custom-theme";
import "./inputs.scss";

interface Props {
  closeMenuOnScroll?: any; // can be bool or func
  defaultOptions?: any[];
  isRequired?: boolean;
  label?: string;
  menuPortalTarget?: any;
  onChange: CallableFunction;
  value?: string | SelectOption;
}

export default function AddressAsyncCreatableInputFilled(props: Props) {
  const geoService: IGeoService = AppContainer.get(GeoService);

  const {
    closeMenuOnScroll,
    defaultOptions,
    isRequired,
    label,
    menuPortalTarget,
    onChange
  } = props;

  const { menu, menuPortal, option } = customStyle;

  const optionStyles: ObjectHash = {
    clearIndicator: (styles: ObjectHash) => ({
      ...styles,
      cursor: "pointer",
      ":hover": {
        color: Colors.red,
        height: "100%"
      }
    }),
    control: (styles: ObjectHash) => ({
      ...styles,
      backgroundColor: "transparent",
      border: "none",
      boxShadow: "none",
      cursor: "text",
      marginTop: "4px",
      minHeight: "none",
      "> div": {
        padding: "0"
      },
      "& input": {
        opacity: "1 !important" // addresses bug where input opacity turns to 0
      }
    }),
    menu: (styles: ObjectHash) => ({
      ...menu(styles),
      overflowY: "hidden"
    }),
    menuPortal,
    option
  };

  const filterAddresses = debounce(
    async (inputValue: string, callback: CallableFunction) => {
      if (inputValue.length < 2) {
        return callback([]);
      }
      const locations: GeoLocation[] = await geoService.getLocations(
        inputValue
      );

      const options = locations.map((location: GeoLocation) => ({
        label: location.description,
        value: location.description,
        meta: location
      }));

      return callback(options);
    },
    300
  );

  const getOptions = (inputValue: string, callback: any) => {
    if (!inputValue) {
      return Promise.resolve({ options: [] });
    }
    return filterAddresses(inputValue, callback);
  };

  return (
    <div className="autocomplete-input--filled">
      {label && (
        <label className="autocomplete-input__label">
          {label}
          {isRequired ? " *" : ""}
        </label>
      )}
      <AsyncCreatableSelect
        {...getSelectValueProps(props)}
        cacheOptions
        className="autocomplete-input autocomplete-input--async address-async-creatable-input-filled"
        closeMenuOnScroll={closeMenuOnScroll}
        components={{
          DropdownIndicator: () => null,
          IndicatorSeparator: () => null,
          LoadingIndicator: () => <Loader type="spinner" />
        }}
        defaultOptions={defaultOptions}
        formatCreateLabel={(label) => `Use custom "${label}"`}
        loadOptions={getOptions}
        maxMenuHeight={240}
        menuPortalTarget={menuPortalTarget}
        noOptionsMessage={({ inputValue }) =>
          inputValue.length ? "No addresses found" : null
        }
        onChange={(option: any) => onChange(option.value, option.meta)}
        onInputChange={(value, { action }) => {
          if (action === "input-change") {
            onChange(value);
          }
        }}
        placeholder=""
        styles={optionStyles}
        tabSelectsValue={false}
        theme={customReactSelectTheme}
      />
    </div>
  );
}
